import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router";
import TopBar from "../../../../../component/TopBar";
import DataTable from "../../../../../component/DataTable";
import Counter from "../../../../../component/Counter";
import InlineMessage from "../../../../../component/InlineMessage";
import { counterRowSx } from "../../../container/frameLayout";
import { getSelfPayslips } from "../../../../../services/payroll";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens } from "../../../../../theme";
import { money, periodLabel } from "../../admin-main/payroll-new/payrollUtils";
import { latestPayslip, yearToDate, paidOn, availableLine, byPeriodDesc } from "./payslipFacts";

/**
 * The employee's own payslips.
 *
 * Was a bare table. The book puts a counter band above it, and the reason is
 * that the question somebody opens this screen with is rarely "what did I earn
 * in October" — it is "what did I last get paid" or "what have I paid in so far
 * this year", and both of those were answerable only by adding up a column by
 * hand.
 *
 * The band deliberately does not match the book's four. The book asks for
 * annual salary, tax paid and pension; this product stores none of those as
 * such — tax and pension would mean matching deduction *names*, which differ
 * per salary structure, and a counter that silently misses a differently-named
 * component is worse than no counter. What is here instead is what the payslips
 * themselves prove.
 */

const dayMonth = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const EmployeePayslips = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getSelfPayslips();
      if (res?.success === false) throw new Error(res?.message || "Failed to load payslips");
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load payslips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const latest = useMemo(() => latestPayslip(rows), [rows]);
  const ytd = useMemo(() => yearToDate(rows), [rows]);
  // Newest first, and published only: an unpublished payslip is a row the
  // employee can open to a figure that may still change.
  const listed = useMemo(() => byPeriodDesc(rows).filter((p) => p.published), [rows]);

  const latestPaid = latest ? dayMonth(paidOn(latest)) : null;

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading="Payslips"
        breadcrumbs={[{ label: "Payslips" }]}
        meta={loading ? null : availableLine(rows)}
      />

      <Box sx={{ ...counterRowSx(4), mb: "1.25rem" }}>
        <Counter
          label="Last net pay"
          value={loading || !latest ? "—" : money(latest.netPay)}
          sub={
            latest
              ? [periodLabel(latest.month, latest.year), latestPaid ? `paid ${latestPaid}` : null]
                  .filter(Boolean).join(", ")
              : "no payslip yet"
          }
        />
        <Counter
          label={ytd ? `Gross ${ytd.year}` : "Gross"}
          value={loading || !ytd ? "—" : money(ytd.gross)}
          sub={ytd ? `across ${ytd.count} payslip${ytd.count === 1 ? "" : "s"}` : null}
        />
        <Counter
          label={ytd ? `Deductions ${ytd.year}` : "Deductions"}
          value={loading || !ytd ? "—" : money(ytd.deductions)}
          sub={ytd ? "tax, contributions and recoveries" : null}
        />
        <Counter
          label={ytd ? `Net paid ${ytd.year}` : "Net paid"}
          value={loading || !ytd ? "—" : money(ytd.net)}
          sub={ytd ? "what reached your account" : null}
        />
      </Box>

      {/* The book's note here says the PDF is password-protected with the
          employee's date of birth. That is not true of this product — a payslip
          is printed from the page, not issued as a locked file — so the note
          says the thing that *is* true and that the reader would otherwise have
          to work out from an absence. */}
      {!loading && !error && (
        <InlineMessage tone="info" sx={{ mb: "1.25rem" }}>
          Only published payslips are listed. A payslip exists as soon as its run is
          calculated, but the figure can still move until payroll publishes it.
        </InlineMessage>
      )}

      <DataTable
        rows={listed}
        loading={loading}
        error={error}
        onRetry={fetchData}
        errorTitle="Couldn't load payslips"
        empty={{
          title: "No payslips yet",
          description: "Your payslips will appear here once payroll is processed.",
        }}
        onRowClick={(p) => navigate(`/app/employee-payslips/${p._id}`)}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          {
            key: "period",
            label: "Period",
            cellSx: { fontWeight: 600 },
            render: (p) => periodLabel(p.month, p.year),
          },
          {
            key: "paid",
            label: "Pay date",
            width: "7rem",
            cellSx: { color: tokens.textMuted },
            render: (p) => dayMonth(paidOn(p)) || "—",
          },
          {
            key: "gross",
            label: "Gross",
            align: "right",
            cellSx: { color: tokens.textMuted },
            render: (p) => money(p.grossPay),
          },
          {
            key: "deductions",
            label: "Deductions",
            align: "right",
            cellSx: { color: tokens.danger },
            render: (p) => money(p.totalDeductions),
          },
          {
            key: "net",
            label: "Net pay",
            align: "right",
            cellSx: { fontWeight: 600 },
            render: (p) => money(p.netPay),
          },
          {
            key: "open",
            label: "",
            width: "5rem",
            align: "right",
            /* The book's last column says "Download". It is "View" here because
               the file is produced from the payslip page itself — labelling it
               Download would promise a file and deliver a screen. */
            render: () => (
              <Box component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.primary }}>
                View
              </Box>
            ),
          },
        ]}
      />
    </Box>
  );
};

export default EmployeePayslips;
